// Provenance: repo:tds-reconciliation-portal:frontend/src/components/ExplainModal.tsx
// (ADR-0005) — restyled on the estate tokens (ADR-0010). Ids became UUID strings; the
// role gate on reason codes now derives from the SAME evaluation the API enforces with
// (lib/identity `roleLabelOf` mirrors the service's compat.role_label).
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { ApiError, postJson } from '../api/client'
import { useDifference } from '../api/hooks'
import { useReasonCodes } from '../api/resolutions'
import vocab from '../api/vocabulary.json'
import { useEvaluation } from '../lib/access'
import { roleLabelOf } from '../lib/identity'
import { Btn, Modal } from './Modal'
import { Money } from './Money'
import { showToast } from './toast'

const FIELD_LABELS: Record<string, string> = {
  je_reference: 'Journal entry reference',
  follow_up_date: 'Follow-up date',
}

interface BulkExplainResult {
  applied: number
  resolution_ids: string[]
  skipped: { fingerprint: string; reason: string }[]
  considered: number
}

/** Why entries were left alone, in the accountant's terms. "Skipped" with no
 *  reason is the kind of message that gets ignored until it matters. */
function skippedReason(out: BulkExplainResult): string {
  const reasons = [...new Set(out.skipped.map((s) => s.reason))]
  return reasons.join(', ')
}

export interface ExplainEntry {
  fingerprint: string
  label: string
  amount: string | null
}

const inputCls =
  'w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-sm outline-none focus:border-primary'

/** In-flow "Explain": attach a reason code to real differences and send them
 *  through maker-checker.
 *
 *  Given a PARTY, the server enumerates its open entries. The predecessor's
 *  modal used to fetch one page of 500, report that page as the total, and
 *  POST once per row in sequence — so a 1,400-row party showed "500 open
 *  entries", explained 500, stayed open, and offered the same 500 again on
 *  reopening. Enumerating the work was never the client's job.
 *
 *  Given ENTRIES, it explains exactly those — the grid's "Explain selected",
 *  and the per-row Explain that finally gives a one-sided entry a clearing
 *  path without leaving the sheet. */
export function ExplainModal({
  runId,
  party,
  entries,
  onClose,
}: {
  runId: string
  /** Explain every open entry of this party. */
  party?: string
  /** …or exactly these entries, selected in the grid. Exactly one of the two. */
  entries?: ExplainEntry[]
  onClose: () => void
}) {
  const { evaluation } = useEvaluation()
  const role = roleLabelOf(evaluation)
  const qc = useQueryClient()
  const reasonsQ = useReasonCodes()
  const selection = entries != null
  // a PREVIEW, not the work list: six rows to show, and `total` for the count.
  // Skipped entirely for a selection, where the caller already knows both.
  const rowsQ = useDifference(selection ? null : runId, {
    bucket: 'unmatched', party, page: 1, size: 6,
  })
  const [reason, setReason] = useState<string | null>(null)
  const [comment, setComment] = useState('')
  const [details, setDetails] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)

  const reasons = reasonsQ.data ?? {}
  const spec = reason ? reasons[reason] : undefined
  const preview: ExplainEntry[] = selection
    ? entries!.slice(0, 6)
    : (rowsQ.data?.items ?? [])
        .filter((r) => r.fingerprint)
        .map((r) => ({
          fingerprint: r.fingerprint!,
          label: `${r.status === 'ONLY_26AS' ? '26AS' : 'Books'} · ${r.party_26as ?? r.party_books ?? ''}`,
          amount: r.tds_26as ?? r.tds_books,
        }))
  const openCount = selection ? entries!.length : (rowsQ.data?.total ?? 0)
  const missing =
    !reason ||
    (spec?.requires_comment && !comment.trim()) ||
    (spec?.requires ?? []).some((f) => !(details[f] ?? '').trim())

  const submit = async () => {
    if (!reason) return
    setBusy(true)
    try {
      // one request, one transaction, and a result that says what it did
      const out = await postJson<BulkExplainResult>(
        `/runs/${runId}/reconciliation/explain/bulk`,
        {
          ...(selection ? { fingerprints: entries!.map((e) => e.fingerprint) }
                        : { party }),
          reason_code: reason,
          comment: comment.trim() || undefined,
          details: Object.keys(details).length ? details : undefined,
        },
      )
      const skipped = out.skipped.length
      showToast({
        color: out.applied ? 'green' : 'yellow',
        title:
          `${out.applied} entr${out.applied === 1 ? 'y' : 'ies'} explained (sent for review)` +
          (skipped ? `; ${skipped} skipped (${skippedReason(out)}).` : '.'),
      })
      for (const k of ['reconciliation', 'difference', 'difference-summary', 'difference-parties', 'match-suggestions', 'cleared']) {
        void qc.invalidateQueries({ queryKey: [k] })
      }
      onClose()
    } catch (e) {
      // nothing was applied — the request is a single transaction — so the
      // modal can stay open on its unchanged input and simply be retried
      showToast({
        color: 'red',
        title: 'Could not explain',
        message: e instanceof ApiError ? e.message : String(e),
      })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal opened onClose={onClose} wide
      title={selection ? `Explain ${entries!.length} selected entr${entries!.length === 1 ? 'y' : 'ies'}`
                       : `Explain — ${party}`}>
      <div className="space-y-3">
        <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
          Attach a reason to {selection ? 'the selected entries' : "this party's open entries"}{' '}
          (timing, booked later, write-off, …). It&apos;s recorded as a difference explanation and
          sent for reviewer approval — the party becomes reconciled once every open entry is
          accounted for.
        </div>

        {!selection && rowsQ.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <div className="space-y-1">
            <p className="text-sm font-semibold text-foreground">
              {openCount} open entr{openCount === 1 ? 'y' : 'ies'}
            </p>
            {preview.map((r) => (
              <div key={r.fingerprint} className="flex items-center justify-between gap-4">
                <p className="truncate text-sm text-foreground">{r.label}</p>
                <Money v={r.amount} />
              </div>
            ))}
            {openCount > preview.length && (
              <p className="text-xs text-muted-foreground">… and {openCount - preview.length} more</p>
            )}
          </div>
        )}

        <label className="block text-sm">
          <span className="mb-1 block font-medium text-foreground">Reason</span>
          <select
            className={inputCls}
            value={reason ?? ''}
            onChange={(e) => setReason(e.target.value || null)}
            required
          >
            <option value="">Pick a reason code</option>
            {Object.entries(reasons).map(([code, s]) => (
              <option key={code} value={code}
                disabled={!!(s.resolver_roles && !s.resolver_roles.includes(role))}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
        {(spec?.requires ?? []).map((f) => (
          <label key={f} className="block text-sm">
            <span className="mb-1 block font-medium text-foreground">{FIELD_LABELS[f] ?? f}</span>
            <input
              className={inputCls}
              type={f === 'follow_up_date' ? 'date' : 'text'}
              value={details[f] ?? ''}
              onChange={(e) => setDetails({ ...details, [f]: e.target.value })}
              required
            />
          </label>
        ))}
        {/* a timing explain always carries the year it points at — left
            blank, the server records the adjacent FY (NEXT_FY = this
            period + 1); typed, the value wins, because a deduction can
            slide two years */}
        {reason != null && (vocab.timing_codes as string[]).includes(reason) && (
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-foreground">Target financial year (optional)</span>
            <input
              className={inputCls}
              placeholder='Blank = the adjacent FY, e.g. "FY 2026-27"'
              value={details.target_period ?? ''}
              onChange={(e) => setDetails({ ...details, target_period: e.target.value })}
            />
          </label>
        )}
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-foreground">
            {spec?.requires_comment ? 'Comment (required)' : 'Comment'}
          </span>
          <textarea
            className={`${inputCls} min-h-16`}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            required={spec?.requires_comment}
          />
        </label>

        <div className="flex justify-end gap-2">
          <Btn onClick={onClose} disabled={busy}>Cancel</Btn>
          <Btn kind="primary" onClick={() => void submit()} loading={busy}
            disabled={Boolean(missing) || openCount === 0}>
            Explain {openCount} entr{openCount === 1 ? 'y' : 'ies'}
          </Btn>
        </div>
      </div>
    </Modal>
  )
}
